// src/components/ai/AIPartRecommendationPanel.js
// Enhanced AI Part Recommendation Panel with modern UI/UX and full theme support

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  useToast,
  Flex,
  Icon,
  IconButton,
  Collapse,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  Progress,
  Heading,
  SimpleGrid,
  Tag,
  TagLabel,
  TagLeftIcon,
  useDisclosure,
  Fade,
  ScaleFade
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdCancel,
  MdLightbulb,
  MdThumbUp,
  MdThumbDown,
  MdExpandMore,
  MdExpandLess,
  MdInventory,
  MdWarning,
  MdInfo
} from 'react-icons/md';
import workOrderInventoryService from '../../services/workOrderInventoryService';

/**
 * AIPartRecommendationPanel Component
 * Displays AI-generated part suggestions for a work order with modern UI
 * Supports accepting/rejecting suggestions and collecting user feedback
 */
const AIPartRecommendationPanel = ({ workOrderId, onUpdate }) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [expandedSuggestions, setExpandedSuggestions] = useState(new Set());
  const [quantities, setQuantities] = useState({}); // Track quantity for each suggestion
  const toast = useToast();

  // ============================================================================
  // THEME COLORS (LIGHT & DARK MODE SUPPORT)
  // ============================================================================
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const cardHoverBg = useColorModeValue('gray.100', 'gray.600');
  const acceptBtnColor = useColorModeValue('green', 'green');
  const rejectBtnColor = useColorModeValue('red', 'red');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const iconColor = useColorModeValue('blue.500', 'blue.200');
  const confidenceBg = useColorModeValue('purple.50', 'purple.900');
  const confidenceColor = useColorModeValue('purple.600', 'purple.200');

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  /**
   * Fetches AI part suggestions from the backend
   */
  const fetchSuggestions = async () => {
    if (!workOrderId) return;

    setIsLoading(true);
    try {
      const data = await workOrderInventoryService.getSuggestions(workOrderId);
      setSuggestions(data || []);
      
      // Initialize quantities for each suggestion
      const initialQuantities = {};
      (data || []).forEach(suggestion => {
        initialQuantities[suggestion.id] = suggestion.quantity_suggested || 1;
      });
      setQuantities(initialQuantities);

    } catch (error) {
      console.error('[AIPartRecommendationPanel] Error fetching suggestions:', error);
      toast({
        title: 'Failed to Load Suggestions',
        description: error.message || 'Could not retrieve AI part recommendations.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [workOrderId]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles accepting an AI suggestion
   */
  const handleAccept = async (suggestion) => {
    if (!suggestion?.id) return;

    setProcessingId(suggestion.id);
    try {
      const quantity = quantities[suggestion.id] || 1;

      await workOrderInventoryService.acceptSuggestion(suggestion.id, {
        work_order_id: workOrderId,
        item_id: suggestion.item_id,
        quantity_used: quantity,
      });

      toast({
        title: 'Part Added Successfully',
        description: `${suggestion.item_name} (${quantity} unit${quantity > 1 ? 's' : ''}) has been added to this work order.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Refresh suggestions and notify parent
      await fetchSuggestions();
      if (onUpdate) onUpdate();

    } catch (error) {
      console.error('[AIPartRecommendationPanel] Error accepting suggestion:', error);
      toast({
        title: 'Failed to Add Part',
        description: error.message || 'Could not add the suggested part.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Handles rejecting an AI suggestion
   */
  const handleReject = async (suggestionId) => {
    if (!suggestionId) return;

    setProcessingId(suggestionId);
    try {
      await workOrderInventoryService.rejectSuggestion(suggestionId);

      toast({
        title: 'Suggestion Rejected',
        description: 'This part suggestion has been dismissed.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Refresh suggestions
      await fetchSuggestions();

    } catch (error) {
      console.error('[AIPartRecommendationPanel] Error rejecting suggestion:', error);
      toast({
        title: 'Failed to Reject',
        description: error.message || 'Could not reject the suggestion.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Handles submitting feedback on a suggestion
   */
  const handleFeedback = async (suggestionId, score) => {
    try {
      await workOrderInventoryService.submitSuggestionFeedback(suggestionId, score);

      toast({
        title: 'Feedback Submitted',
        description: 'Thank you! Your feedback helps improve our AI.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

    } catch (error) {
      console.error('[AIPartRecommendationPanel] Error submitting feedback:', error);
      // Silent fail for feedback - don't disrupt user flow
    }
  };

  /**
   * Toggles the expanded state of a suggestion
   */
  const toggleExpanded = (suggestionId) => {
    setExpandedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(suggestionId)) {
        newSet.delete(suggestionId);
      } else {
        newSet.add(suggestionId);
      }
      return newSet;
    });
  };

  /**
   * Updates the quantity for a suggestion
   */
  const handleQuantityChange = (suggestionId, value) => {
    setQuantities(prev => ({
      ...prev,
      [suggestionId]: parseInt(value) || 1
    }));
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Gets the confidence color based on score
   */
  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'blue';
    if (score >= 0.4) return 'yellow';
    return 'orange';
  };

  /**
   * Formats confidence score as percentage
   */
  const formatConfidence = (score) => {
    return `${Math.round(score * 100)}%`;
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  /**
   * Renders a single suggestion card
   */
  const renderSuggestionCard = (suggestion) => {
    const isExpanded = expandedSuggestions.has(suggestion.id);
    const isProcessing = processingId === suggestion.id;
    const quantity = quantities[suggestion.id] || 1;
    const isLowStock = suggestion.stock_level < 5;

    return (
      <ScaleFade key={suggestion.id} in={true} initialScale={0.95}>
        <Box
          bg={cardBg}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          p={4}
          transition="all 0.2s"
          _hover={{
            bg: cardHoverBg,
            transform: 'translateY(-2px)',
            shadow: 'md'
          }}
        >
          {/* Header Section */}
          <Flex justify="space-between" align="flex-start" mb={3}>
            <HStack spacing={3} flex={1}>
              {/* Icon */}
              <Flex
                bg={iconBg}
                p={2}
                borderRadius="md"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdInventory} color={iconColor} boxSize={5} />
              </Flex>

              {/* Part Name and Details */}
              <VStack align="flex-start" spacing={0} flex={1}>
                <Text fontWeight="bold" fontSize="md" color={headingColor}>
                  {suggestion.item_name}
                </Text>
                <HStack spacing={2}>
                  {/* Confidence Badge */}
                  <Tooltip label="AI Confidence Score" placement="top">
                    <Tag size="sm" colorScheme={getConfidenceColor(suggestion.confidence_score)} variant="subtle">
                      <TagLeftIcon as={MdLightbulb} />
                      <TagLabel>{formatConfidence(suggestion.confidence_score)}</TagLabel>
                    </Tag>
                  </Tooltip>

                  {/* Stock Level Warning */}
                  {isLowStock && (
                    <Tooltip label="Low Stock Alert" placement="top">
                      <Tag size="sm" colorScheme="orange" variant="subtle">
                        <TagLeftIcon as={MdWarning} />
                        <TagLabel>{suggestion.stock_level} left</TagLabel>
                      </Tag>
                    </Tooltip>
                  )}
                </HStack>
              </VStack>
            </HStack>

            {/* Expand/Collapse Button */}
            <IconButton
              icon={<Icon as={isExpanded ? MdExpandLess : MdExpandMore} />}
              size="sm"
              variant="ghost"
              onClick={() => toggleExpanded(suggestion.id)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            />
          </Flex>

          {/* Expanded Details */}
          <Collapse in={isExpanded} animateOpacity>
            <VStack align="stretch" spacing={3} mt={3} pt={3} borderTopWidth="1px" borderColor={borderColor}>
              {/* Stock Information */}
              <SimpleGrid columns={2} spacing={2}>
                <Box>
                  <Text fontSize="xs" color={textColor} mb={1}>Stock Available</Text>
                  <Text fontSize="sm" fontWeight="semibold" color={headingColor}>
                    {suggestion.stock_level} units
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={textColor} mb={1}>Confidence</Text>
                  <Progress
                    value={suggestion.confidence_score * 100}
                    size="sm"
                    colorScheme={getConfidenceColor(suggestion.confidence_score)}
                    borderRadius="full"
                  />
                </Box>
              </SimpleGrid>

              {/* Quantity Selector */}
              <Box>
                <Text fontSize="xs" color={textColor} mb={2}>Quantity to Use</Text>
                <NumberInput
                  value={quantity}
                  min={1}
                  max={suggestion.stock_level}
                  onChange={(valueString) => handleQuantityChange(suggestion.id, valueString)}
                  size="sm"
                  isDisabled={isProcessing}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
            </VStack>
          </Collapse>

          <Divider my={3} />

          {/* Action Buttons */}
          <HStack spacing={2} justify="space-between">
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme={acceptBtnColor}
                leftIcon={<Icon as={MdCheckCircle} />}
                onClick={() => handleAccept(suggestion)}
                isLoading={isProcessing}
                isDisabled={isProcessing || quantity > suggestion.stock_level}
              >
                Accept
              </Button>
              <Button
                size="sm"
                colorScheme={rejectBtnColor}
                variant="outline"
                leftIcon={<Icon as={MdCancel} />}
                onClick={() => handleReject(suggestion.id)}
                isLoading={isProcessing}
                isDisabled={isProcessing}
              >
                Reject
              </Button>
            </HStack>

            {/* Feedback Buttons */}
            <HStack spacing={1}>
              <Tooltip label="This suggestion is helpful">
                <IconButton
                  icon={<Icon as={MdThumbUp} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="green"
                  onClick={() => handleFeedback(suggestion.id, 1)}
                  isDisabled={isProcessing}
                  aria-label="Helpful"
                />
              </Tooltip>
              <Tooltip label="This suggestion is not helpful">
                <IconButton
                  icon={<Icon as={MdThumbDown} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleFeedback(suggestion.id, -1)}
                  isDisabled={isProcessing}
                  aria-label="Not helpful"
                />
              </Tooltip>
            </HStack>
          </HStack>
        </Box>
      </ScaleFade>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <VStack spacing={3}>
          <Spinner size="lg" color="blue.500" thickness="3px" />
          <Text color={textColor} fontSize="sm">Loading AI recommendations...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <Alert
        status="info"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        minH="200px"
        borderRadius="lg"
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          No Part Suggestions Available
        </AlertTitle>
        <AlertDescription maxW="sm" fontSize="sm">
          Our AI hasn't generated any part recommendations for this work order yet. 
          Suggestions will appear here once the AI analyzes the work order details.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={4}>
        <HStack spacing={2}>
          <Icon as={MdLightbulb} color={iconColor} boxSize={6} />
          <Heading size="md" color={headingColor}>
            AI Part Recommendations
          </Heading>
          <Badge colorScheme="purple" fontSize="xs">
            {suggestions.length} {suggestions.length === 1 ? 'suggestion' : 'suggestions'}
          </Badge>
        </HStack>
      </Flex>

      {/* Info Box */}
      <Alert status="info" variant="left-accent" mb={4} borderRadius="md">
        <AlertIcon as={MdInfo} />
        <Box flex={1}>
          <Text fontSize="sm">
            These parts were identified by AI based on similar work orders and issue patterns. 
            Review each suggestion and accept those you need.
          </Text>
        </Box>
      </Alert>

      {/* Suggestions List */}
      <VStack spacing={3} align="stretch">
        {suggestions.map(suggestion => renderSuggestionCard(suggestion))}
      </VStack>
    </Box>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

AIPartRecommendationPanel.propTypes = {
  workOrderId: PropTypes.string.isRequired,
  onUpdate: PropTypes.func, // Callback when suggestions are accepted/rejected
};

export default AIPartRecommendationPanel;